/**
 * The actions of a notification (I-2915): the buttons it offers besides
 * opening it — approve a request, accept an invitation. An action either
 * calls the API of a module (`request`) or opens something (`link`). Master
 * only stores the descriptor: the request is performed here, with the user's
 * own session, through the shell's one way to a module's API
 * (`moduleApi.ts`), so the module that owns the object checks the rights.
 *
 * After a request that succeeded, or a link that opened, the action is
 * reported to master (`POST /notifications/:id/complete`): the notification
 * becomes resolved and read on every device. A request that failed shows the
 * module's own message and leaves the notification as it is.
 *
 * Two things are checked before a request leaves:
 *
 * - **Its shape** (`isModuleApiWrite`): a module key, a writing method and a
 *   plain path inside the module's API — master's own rule, once more. A
 *   request that fails it is not sent.
 * - **Its company.** A module answers in the company the session works in,
 *   but a notification that names a company (`companyID`) belongs to that
 *   one. The shell cannot send one request in another company: a company
 *   switch moves the whole session and reloads the shell (`scope.ts`). So an
 *   action of another company's notification is not sent from here — the
 *   notification's link is opened instead and the user confirms there. Mail,
 *   calendar and chat belong to the account: their notifications name no
 *   company (0), and their actions are sent from wherever the session works.
 *
 * Shared by the bell's panel and the notifications page, so a row offers the
 * same in both.
 */
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useNxToasts, type NxButtonVariant } from "@blpde/nx-ui";
import { readProblemBody } from "@blpde/connect/http";
import { isModuleApiWrite, moduleApiRequest } from "@/moduleApi";
import { openNotificationLink } from "@/notificationLinks";
import {
  completeNotification,
  type MasterNotification,
  type NotificationAction,
} from "@/notifications";
import { useNotificationText } from "@/notificationText";
import { sessionCompanyID } from "@/scope";

/** What running an action came to. */
export interface NotificationActionResult {
  /** The action was performed (and reported to master). */
  done: boolean;
  /**
   * The action was not performed here: it belongs to another company, and
   * the notification was opened there instead for the user to confirm.
   */
  redirected?: boolean;
  /**
   * The notification as master holds it afterwards; null when master changed
   * nothing (the module had resolved it already) or the action failed.
   */
  notification: MasterNotification | null;
}

/** The actions a row offers: none once it is dealt with. */
export function notificationActionsOf(
  n: MasterNotification,
): NotificationAction[] {
  if (n.resolvedAt) return [];
  return (n.actions ?? []).filter(
    (action) => !!action?.key && (!!action.request || !!action.link),
  );
}

/** The kit button an action's style asks for: prominent, destructive, or plain. */
export function notificationActionVariant(
  action: Pick<NotificationAction, "style">,
): NxButtonVariant {
  if (action.style === "primary") return "default";
  if (action.style === "danger") return "outline-danger";
  return "outline";
}

/** The module's own words for a failed request; empty when it sent none. */
async function failureMessage(res: Response): Promise<string> {
  let text = "";
  try {
    text = await res.text();
  } catch {
    // The status alone, then.
  }
  const problem = readProblemBody(res.status, text, res.headers);
  return (problem?.detail || problem?.title || "").trim();
}

/** How one attempt at an action ended. */
type Outcome = "done" | "failed" | "redirected";

export function useNotificationActions() {
  const { t } = useI18n();
  const router = useRouter();
  const { showToast } = useNxToasts();
  const { resolvedLabelOf } = useNotificationText();

  /**
   * The company the session works in; 0 when master does not say. `fresh`
   * asks master again: the session may have moved since the shell read it.
   */
  async function activeCompanyID(fresh = false): Promise<number> {
    return (await sessionCompanyID(fresh)) ?? 0;
  }

  /** The rows with an action on its way: one request per row at a time. */
  const running = ref<Map<number, string>>(new Map());

  function setRunning(id: number, key: string | null): void {
    const next = new Map(running.value);
    if (key === null) next.delete(id);
    else next.set(id, key);
    running.value = next;
  }

  /** The key of the action a row is performing; undefined while it rests. */
  function runningAction(n: MasterNotification): string | undefined {
    return running.value.get(n.id);
  }

  /**
   * An action of another company's notification: opened where it belongs
   * instead of sent from here, with a word on what to do there.
   */
  async function confirmInItsCompany(n: MasterNotification): Promise<Outcome> {
    const opened = await openNotificationLink(router, n.link, {
      companyID: n.companyID,
    });
    if (!opened) {
      showToast(t("connect.notifications.actionOtherCompanyFailed"), "error");
      return "failed";
    }
    showToast({
      message: t("connect.notifications.actionOtherCompany"),
      kind: "info",
      timeout: 8000,
    });
    return "redirected";
  }

  async function perform(
    n: MasterNotification,
    action: NotificationAction,
  ): Promise<Outcome> {
    if (action.request) {
      // Never sent as stored without a look: master's rule, once more.
      if (!isModuleApiWrite(action.request)) {
        showToast(t("connect.notifications.actionFailed"), "error");
        return "failed";
      }
      if (n.companyID) {
        let active = await activeCompanyID();
        // What the shell knows may be old: ask before turning the user away.
        if (active && active !== n.companyID) active = await activeCompanyID(true);
        // Without knowing where the session works, nothing is sent blind.
        if (!active) {
          showToast(t("connect.notifications.actionFailed"), "error");
          return "failed";
        }
        if (active !== n.companyID) return confirmInItsCompany(n);
      }
      const { module, method, path, body } = action.request;
      const hasBody = body !== undefined && body !== null;
      let res: Response;
      try {
        res = await moduleApiRequest(module, path, {
          method: method.toUpperCase(),
          headers: {
            Accept: "application/json",
            ...(hasBody ? { "Content-Type": "application/json" } : {}),
          },
          ...(hasBody ? { body: JSON.stringify(body) } : {}),
        });
      } catch {
        showToast(t("connect.notifications.actionFailed"), "error");
        return "failed";
      }
      if (!res.ok) {
        showToast(
          (await failureMessage(res)) ||
            t("connect.notifications.actionFailed"),
          "error",
        );
        return "failed";
      }
      return "done";
    }
    const opened = await openNotificationLink(router, action.link, {
      companyID: n.companyID,
    });
    if (!opened) showToast(t("connect.notifications.linkFailed"), "error");
    return opened ? "done" : "failed";
  }

  /**
   * Performs an action of a row and reports it to master. A second press
   * while one is on its way does nothing.
   */
  async function run(
    n: MasterNotification,
    action: NotificationAction,
  ): Promise<NotificationActionResult> {
    if (running.value.has(n.id)) return { done: false, notification: null };
    setRunning(n.id, action.key);
    try {
      const outcome = await perform(n, action);
      if (outcome !== "done") {
        return {
          done: false,
          notification: null,
          ...(outcome === "redirected" ? { redirected: true } : {}),
        };
      }
      let notification: MasterNotification | null = null;
      try {
        notification = await completeNotification(n.id, action.key);
      } catch {
        // The action itself went through; the module resolves what it owns,
        // and the next reload shows what master has.
      }
      showToast(
        t("connect.notifications.resolvedWith", {
          action: resolvedLabelOf(n, action),
        }),
      );
      return { done: true, notification };
    } finally {
      setRunning(n.id, null);
    }
  }

  return { run, runningAction };
}
